import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import ProductCard from '../components/ProductCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { CATEGORIES, findCategoryName } from '../constants/categories';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    is_visible: true,
    is_quote_only: false,
    category: '',
    subcategory: '',
    is_new: false,
    is_sale: false,
    old_price: ''
  });
  const [imageFile, setImageFile] = useState(null);

  const fetchProducts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/products`, { withCredentials: true });
      const sorted = [...data].sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', 'ro', { sensitivity: 'base' })
      );
      setProducts(sorted);
    } catch (error) {
      toast.error('Eroare la încărcarea produselor');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const selectedCategoryData = CATEGORIES.find((c) => c.id === formData.category);
  const availableSubcategories = selectedCategoryData?.subcategories || [];

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        ...formData,
        price: formData.is_quote_only ? 0 : parseFloat(formData.price || 0),
        category: formData.category || null,
        subcategory: formData.subcategory || null,
        old_price: formData.is_sale && formData.old_price ? parseFloat(formData.old_price) : null
      };

      let productId = editProduct?.id;

      if (editProduct) {
        await axios.put(`${API}/admin/products/${editProduct.id}`, payload, {
          withCredentials: true
        });
        toast.success('Produs actualizat cu succes');
      } else {
        const { data } = await axios.post(`${API}/admin/products`, payload, {
          withCredentials: true
        });
        productId = data.id;
        toast.success('Produs adăugat cu succes');
      }

      if (imageFile && productId) {
        const formDataImg = new FormData();
        formDataImg.append('file', imageFile);
        await axios.post(`${API}/admin/products/${productId}/image`, formDataImg, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      setOpen(false);
      setFormData({ name: '', description: '', price: '', is_visible: true, is_quote_only: false, category: '', subcategory: '', is_new: false, is_sale: false, old_price: '' });
      setImageFile(null);
      setEditProduct(null);
      fetchProducts();
    } catch (error) {
      toast.error('Eroare la salvarea produsului');
    }
  };

  const handleEdit = (product) => {
    setEditProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      is_visible: product.is_visible,
      is_quote_only: product.is_quote_only || false,
      category: product.category || '',
      subcategory: product.subcategory || '',
      is_new: product.is_new || false,
      is_sale: product.is_sale || false,
      old_price: product.old_price ? product.old_price.toString() : ''
    });
    setOpen(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Sigur vrei să ștergi acest produs?')) return;

    try {
      await axios.delete(`${API}/admin/products/${productId}`, { withCredentials: true });
      toast.success('Produs șters cu succes');
      fetchProducts();
    } catch (error) {
      toast.error('Eroare la ștergerea produsului');
    }
  };

  const toggleVisibility = async (product) => {
    try {
      await axios.put(
        `${API}/admin/products/${product.id}`,
        { is_visible: !product.is_visible },
        { withCredentials: true }
      );
      toast.success(`Produs ${!product.is_visible ? 'afișat' : 'ascuns'}`);
      fetchProducts();
    } catch (error) {
      toast.error('Eroare la actualizarea vizibilității');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <p style={{ fontFamily: 'IBM Plex Sans' }}>Se încarcă...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 gap-4">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
            Gestionare Produse
          </h1>

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-green-600 hover:bg-green-700 text-white"
                onClick={() => {
                  setEditProduct(null);
                  setFormData({ name: '', description: '', price: '', is_visible: true, is_quote_only: false, category: '', subcategory: '', is_new: false, is_sale: false, old_price: '' });
                  setImageFile(null);
                }}
                data-testid="add-product-button"
              >
                <Plus className="w-5 h-5 mr-2" />
                Adaugă Produs
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Outfit' }}>
                  {editProduct ? 'Editează Produs' : 'Adaugă Produs Nou'}
                </DialogTitle>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="space-y-6 mt-4">
                <div>
                  <Label htmlFor="name">Nume Produs</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="product-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="description">Descriere</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                    rows={3}
                    data-testid="product-description-input"
                  />
                </div>

                <div>
                  <Label htmlFor="price">Preț (RON)</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    disabled={formData.is_quote_only}
                    required={!formData.is_quote_only}
                    placeholder={formData.is_quote_only ? '(Nu se aplică - Cere ofertă)' : ''}
                    data-testid="product-price-input"
                  />
                </div>

                <div className="flex items-center space-x-3 p-3 bg-orange-50 border border-orange-200 rounded-md">
                  <Switch
                    id="is_quote_only"
                    checked={formData.is_quote_only}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, is_quote_only: checked, price: checked ? '' : formData.price })
                    }
                    data-testid="product-quote-only-switch"
                  />
                  <Label htmlFor="is_quote_only" className="cursor-pointer text-orange-800 font-semibold">
                    Cere ofertă (ascunde prețul, clientul contactează pentru ofertă)
                  </Label>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="flex items-center space-x-3 p-3 bg-green-50 border border-green-200 rounded-md">
                    <Switch
                      id="is_new"
                      checked={formData.is_new}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_new: checked })}
                      data-testid="product-is-new-switch"
                    />
                    <Label htmlFor="is_new" className="cursor-pointer text-green-800 font-semibold">
                      Badge &quot;NOU&quot;
                    </Label>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-red-50 border border-red-200 rounded-md">
                    <Switch
                      id="is_sale"
                      checked={formData.is_sale}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_sale: checked, old_price: checked ? formData.old_price : '' })}
                      data-testid="product-is-sale-switch"
                    />
                    <Label htmlFor="is_sale" className="cursor-pointer text-red-800 font-semibold">
                      Badge &quot;REDUS&quot;
                    </Label>
                  </div>
                </div>

                {formData.is_sale && !formData.is_quote_only && (
                  <div>
                    <Label htmlFor="old_price">Preț vechi (RON) - opțional, pentru procentul de reducere</Label>
                    <Input
                      id="old_price"
                      type="number"
                      step="0.01"
                      value={formData.old_price}
                      onChange={(e) => setFormData({ ...formData, old_price: e.target.value })}
                      placeholder="Ex: 100"
                      data-testid="product-old-price-input"
                    />
                  </div>
                )}

                <div>
                  <Label htmlFor="category">Categorie</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value, subcategory: '' })}
                  >
                    <SelectTrigger data-testid="product-category-select">
                      <SelectValue placeholder="Alege categorie..." />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {availableSubcategories.length > 0 && (
                  <div>
                    <Label htmlFor="subcategory">Subcategorie</Label>
                    <Select
                      value={formData.subcategory}
                      onValueChange={(value) => setFormData({ ...formData, subcategory: value })}
                    >
                      <SelectTrigger data-testid="product-subcategory-select">
                        <SelectValue placeholder="Alege subcategorie..." />
                      </SelectTrigger>
                      <SelectContent>
                        {availableSubcategories.map((sub) => (
                          <SelectItem key={sub} value={sub}>
                            {sub}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div>
                  <Label htmlFor="image">Imagine Produs</Label>
                  <Input
                    id="image"
                    type="file"
                    accept="image/*"
                    onChange={(e) => setImageFile(e.target.files[0])}
                    data-testid="product-image-input"
                  />
                </div>

                <div className="flex items-center space-x-3">
                  <Switch
                    id="is_visible"
                    checked={formData.is_visible}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_visible: checked })}
                    data-testid="product-visibility-switch"
                  />
                  <Label htmlFor="is_visible" className="cursor-pointer">
                    Afișează produs clienților
                  </Label>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-blue-900 hover:bg-blue-800 text-white"
                  data-testid="submit-product-button"
                >
                  {editProduct ? 'Actualizează' : 'Adaugă'} Produs
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {products.length === 0 ? (
          <p className="text-slate-600" style={{ fontFamily: 'IBM Plex Sans' }}>
            Nu există produse adăugate încă.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {products.map((product) => (
              <Card
                key={product.id}
                className="overflow-hidden border border-slate-200 shadow-sm"
                data-testid={`admin-product-${product.id}`}
              >
                <ProductCard product={product} isAdmin={true} />

                <CardContent className="p-4 pt-0 space-y-3">
                  {product.category && (
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="outline" className="bg-blue-50 text-blue-900 border-blue-200">
                        {findCategoryName(product.category)}
                      </Badge>
                      {product.subcategory && (
                        <Badge variant="outline" className="bg-orange-50 text-orange-800 border-orange-200">
                          {product.subcategory}
                        </Badge>
                      )}
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Vizibil:</Label>
                    <Switch
                      checked={product.is_visible}
                      onCheckedChange={() => toggleVisibility(product)}
                      data-testid={`toggle-visibility-${product.id}`}
                    />
                  </div>

                  <div className="flex space-x-2">
                    <Button
                      onClick={() => handleEdit(product)}
                      className="flex-1 bg-blue-900 hover:bg-blue-800 text-white"
                      size="sm"
                      data-testid={`edit-product-${product.id}`}
                    >
                      <Pencil className="w-4 h-4 mr-1" />
                      Editează
                    </Button>
                    <Button
                      onClick={() => handleDelete(product.id)}
                      variant="destructive"
                      size="sm"
                      data-testid={`delete-product-${product.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminProducts;
